import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  Skeleton,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { brandColors } from '../../theme';
import { useIdeas } from '../../hooks/useIdeaBoard';
import { useCurrentUser } from '../../hooks/useApi';
import IdeaList from '../../components/idea-board/IdeaList';
import SubmitIdeaModal from '../../components/idea-board/SubmitIdeaModal';
import AdminFeatureUpdateModal from '../../components/idea-board/AdminFeatureUpdateModal';

const IdeaBoardPage: React.FC = () => {
  const [submitOpen, setSubmitOpen] = useState(false);
  const [adminUpdateOpen, setAdminUpdateOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: user } = useCurrentUser();
  const { data: ideas, isLoading, isError, refetch } = useIdeas();

  const isAdmin =
    user?.permissions?.includes('market_compass.admin') || user?.role === 'admin';

  const handleSubmitSuccess = () => {
    setSnackbar({ open: true, message: 'Your idea has been submitted successfully!', severity: 'success' });
  };

  const handleAdminUpdateSuccess = () => {
    setSnackbar({ open: true, message: 'Feature update created successfully!', severity: 'success' });
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        {/* Page header */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: brandColors.indigo, lineHeight: 1.2 }}
              >
                Idea Board
              </Typography>
              <Typography variant="body1" sx={{ color: brandColors.mediumGray, mt: 0.75 }}>
                Share your ideas, vote on what matters most, and track what we've shipped.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              {isAdmin && (
                <Button
                  variant="outlined"
                  startIcon={<AnnouncementOutlinedIcon />}
                  onClick={() => setAdminUpdateOpen(true)}
                  sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Post Update
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setSubmitOpen(true)}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                Submit an Idea
              </Button>
            </Box>
          </Box>

          <Divider />
        </Box>

        {/* Content */}
        {isLoading && (
          <Box>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Skeleton variant="text" width="40%" height={32} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={80} sx={{ mb: 1 }} />
                <Skeleton variant="rounded" height={80} sx={{ mb: 1 }} />
              </Box>
            ))}
          </Box>
        )}

        {isError && !isLoading && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            Failed to load ideas. Please try again.
          </Alert>
        )}

        {!isLoading && !isError && (
          <IdeaList ideas={ideas || []} isAdmin={!!isAdmin} />
        )}

        {/* Footer link to recent updates */}
        <Box
          sx={{
            mt: 5,
            pt: 3,
            borderTop: `1px solid`,
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Button
            component={Link}
            to="/idea-board/recent-updates"
            endIcon={<ArrowForwardIcon />}
            sx={{ textTransform: 'none', color: brandColors.blue }}
          >
            View Recent Updates &amp; Completed Ideas
          </Button>
        </Box>
      </Container>

      <SubmitIdeaModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSuccess={handleSubmitSuccess}
      />

      <AdminFeatureUpdateModal
        open={adminUpdateOpen}
        onClose={() => setAdminUpdateOpen(false)}
        onSuccess={handleAdminUpdateSuccess}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default IdeaBoardPage;
